import { Link } from "@tanstack/react-router";
import { ArrowRight, BookOpen, Mail, ShieldCheck, Sparkles } from "lucide-react";
import { BLOG_CATEGORIES } from "@/data/blog-posts";
import { COMPANY } from "@/config/company";
import { Button } from "@/components/ui/button";
import { NewsletterSignup } from "@/components/site/NewsletterSignup";
import { PreferredSourceLink } from "@/components/site/PreferredSourceLink";
import { ArticleShareFooter } from "./ArticleFurniture";
import { BlogPostImage } from "./BlogPostImage";
import type { PublicBlogPost } from "@/lib/blog-public.functions";

export type ArticleEndingPost = Pick<PublicBlogPost, "slug" | "title" | "excerpt" | "category" | "article_role">
  & Partial<Pick<PublicBlogPost, "hero_image_url" | "cover_url" | "cover_alt" | "published_at" | "read_mins">>;

function ArticleCards({ posts, compact = false }: { posts: ArticleEndingPost[]; compact?: boolean }) {
  return (
    <div className={`mt-5 grid gap-4 ${compact ? "sm:grid-cols-2" : "md:grid-cols-3"}`}>
      {posts.map((post) => (
        <article key={post.slug} className="group flex min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-card shadow-sm transition hover:-translate-y-0.5 hover:border-brand-gold/70 hover:shadow-md">
          <BlogPostImage
            src={post.hero_image_url ?? post.cover_url}
            alt={post.cover_alt}
            title={post.title}
            category={post.category}
            className="aspect-[16/9] w-full object-cover"
          />
          <div className="flex flex-1 flex-col p-5">
            <p className="text-[11px] font-bold uppercase tracking-widest text-brand">
              {post.article_role === "pillar" ? "Topic guide" : BLOG_CATEGORIES.find((category) => category.slug === post.category)?.name ?? "Investor guide"}
            </p>
            <h3 className="mt-2 text-lg font-bold leading-snug text-foreground">{post.title}</h3>
            {post.excerpt ? <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-muted-foreground">{post.excerpt}</p> : null}
            <Link to="/blog/$slug" params={{ slug: post.slug }} className="mt-auto inline-flex min-h-11 items-center gap-1 pt-4 text-sm font-bold text-brand underline decoration-brand-gold decoration-2 underline-offset-4">
              Read guide <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden />
            </Link>
          </div>
        </article>
      ))}
    </div>
  );
}

export function ArticleEnding({
  path,
  title,
  updatedAt,
  expertInsight,
  clusterPosts = [],
  relatedPosts = [],
  showPreferredSource = true,
}: {
  path: string;
  title: string;
  updatedAt?: string | null;
  expertInsight?: string | null;
  clusterPosts?: ArticleEndingPost[];
  relatedPosts?: ArticleEndingPost[];
  showPreferredSource?: boolean;
}) {
  const related = relatedPosts.filter((post) => !clusterPosts.some((cluster) => cluster.slug === post.slug));
  const reviewed = updatedAt
    ? new Date(updatedAt.includes("T") ? updatedAt : `${updatedAt}T00:00:00Z`).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" })
    : null;

  return (
    <div className="mt-14 space-y-12" data-article-ending>
      <aside className="relative overflow-hidden rounded-xl border border-border bg-muted/30 shadow-[0_4px_20px_-4px_hsl(var(--foreground)/0.08)]" aria-labelledby="expert-insight-heading">
        <span className="absolute inset-y-0 left-0 w-1.5 bg-brand-gold" aria-hidden />
        <div className="p-6 pl-8 md:p-10 md:pl-12">
          <div className="flex items-center gap-3">
            <span className="h-px w-6 bg-brand-gold" aria-hidden />
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-brand md:text-xs">Mortgage strategy</p>
          </div>
          <h2 id="expert-insight-heading" className="mt-4 font-display text-2xl italic text-brand md:text-3xl">Expert insight</h2>
          <p className="mt-6 text-lg leading-relaxed text-foreground/80">
            {expertInsight || "Treat the numbers in this guide as a starting point. A licensed loan officer can review the property’s rent, full payment, leverage, reserves, and loan structure against current lender guidelines before you make a financing decision."}
          </p>
          <div className="mt-10 flex flex-col gap-3 border-t border-border pt-7 md:flex-row md:items-center md:gap-4">
            <div className="flex items-center gap-3">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-brand shadow-sm">
                <ShieldCheck className="h-5 w-5 text-brand-gold" aria-hidden />
              </span>
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground md:text-xs">
                Editorial review by <span className="font-semibold text-foreground">{COMPANY.companyLegalName}</span>
              </p>
            </div>
            <span className="hidden h-4 w-px bg-border md:block" aria-hidden />
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground md:text-xs">
              NMLS #{COMPANY.companyNmlsId}{reviewed ? ` · Updated ${reviewed}` : ""} · No individual expert or outcome is implied
            </p>
          </div>
        </div>
      </aside>

      {clusterPosts.length ? (
        <section aria-labelledby="topic-cluster-heading">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-brand">
            <BookOpen className="h-4 w-4 text-brand-gold" aria-hidden /> Explore this topic
          </div>
          <h2 id="topic-cluster-heading" className="mt-2 font-display text-2xl font-bold text-foreground">More from this guide series</h2>
          <p className="mt-2 text-sm text-muted-foreground">Published articles from the same research cluster.</p>
          <ArticleCards posts={clusterPosts} compact />
        </section>
      ) : null}

      {related.length ? (
        <section aria-labelledby="related-articles-heading">
          <p className="text-xs font-bold uppercase tracking-widest text-brand">Continue your research</p>
          <h2 id="related-articles-heading" className="mt-2 font-display text-2xl font-bold text-foreground">Related investor guides</h2>
          <ArticleCards posts={related} />
        </section>
      ) : null}

      <aside className="overflow-hidden rounded-2xl border border-brand-gold/60 bg-brand-gradient text-brand-foreground shadow-xl" aria-labelledby="article-cta-heading">
        <div className="grid gap-8 p-6 md:grid-cols-[minmax(0,1fr)_minmax(17rem,0.85fr)] md:items-center md:p-9">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-brand-gold/50 bg-brand-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-brand-gold">
              <Sparkles className="h-3.5 w-3.5" aria-hidden /> Your next step
            </div>
            <h2 id="article-cta-heading" className="mt-4 font-display text-2xl font-bold md:text-3xl">Turn the research into a reviewed scenario.</h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-brand-foreground/80">
              Share your non-owner-occupied investment property scenario. A licensed loan officer can review it against available business-purpose programs.
            </p>
            <Button asChild size="lg" variant="secondary" className="mt-6 bg-brand-gold font-bold text-brand hover:bg-brand-gold/90">
              <Link to="/apply" search={{ source: "blog", goal: "scenario-review" } as never}>Start My Approval</Link>
            </Button>
            <p className="mt-3 text-[11px] leading-relaxed text-brand-foreground/65">No guaranteed approval. Submitting the form is not an offer of credit or commitment to lend.</p>
          </div>
          <div className="rounded-xl border border-brand-foreground/15 bg-background p-5 text-foreground shadow-lg">
            <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-brand">
              <Mail className="h-4 w-4 text-brand-gold" aria-hidden /> Investor brief
            </div>
            <NewsletterSignup variant="article" />
          </div>
        </div>
      </aside>

      <ArticleShareFooter path={path} title={title} />

      <aside className="rounded-xl border border-border bg-muted/35 p-5 text-xs leading-relaxed text-muted-foreground" aria-label="Article disclosure">
        <div className="flex items-start gap-3">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-brand" aria-hidden />
          <p>
            <strong className="text-foreground">Educational and business-purpose information.</strong> {COMPANY.companyLegalName} (NMLS #{COMPANY.companyNmlsId}) is a mortgage broker, not a lender. DSCR content concerns eligible non-owner-occupied investment property. It is not an offer of credit, commitment to lend, tax, legal, or investment advice. Rates, fees, terms, programs, and eligibility vary and remain subject to lender guidelines and underwriting. Residential mortgage activity is limited to Florida ({COMPANY.floridaLicense.number}) and Colorado ({COMPANY.coloradoRegistration.label}). This site is not approved by or affiliated with any government agency. Review our <Link to="/legal/disclaimers" className="font-semibold text-brand underline underline-offset-2">full disclaimers</Link> and <Link to="/legal/licensing" className="font-semibold text-brand underline underline-offset-2">licensing information</Link>.
          </p>
        </div>
      </aside>

      {showPreferredSource ? <PreferredSourceLink surface="blog-post" variant="card" /> : null}
    </div>
  );
}